import { FieldValues, Control, Path } from 'react-hook-form';
import * as React from 'react';

import {
  ComboboxPlaceholder,
  ComboboxContent,
  ComboboxTrigger,
  ComboboxValue,
  ComboboxInput,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  Combobox,
} from '../combobox/combobox';
import { FormControl, FormMessage, FormField, FormItem } from '../form';

interface IFormComboboxProps<T extends FieldValues = FieldValues> {
  componentProps?: Partial<React.ComponentProps<typeof Combobox>> & { hasShadow?: boolean };
  options: { keywords?: string[]; disabled?: boolean; label: string; value: string }[];
  size?: 'medium' | 'small' | 'large';
  hideErrorMessage?: boolean;
  formControl: Control<T>;
  emptyContent?: string;
  placeholder?: string;
  disabled?: boolean;
  floating?: boolean;
  className?: string;
  required?: boolean;
  name: Path<T>;
}

export function FormCombobox<T extends FieldValues>({
  hideErrorMessage,
  componentProps,
  emptyContent,
  placeholder,
  formControl,
  className,
  floating,
  disabled,
  required,
  options,
  size,
  name,
}: IFormComboboxProps<T>) {
  const { hasShadow, ...componentPropsRest } = componentProps || {};

  return (
    <FormField
      render={({ field, formState }) => {
        const hasError = !!formState.errors[field.name];
        return (
          <FormItem className={className}>
            <FormControl>
              <Combobox
                onSelectedChange={field.onChange}
                selected={field.value}
                {...componentPropsRest}
              >
                <ComboboxTrigger
                  hasShadow={hasShadow}
                  disabled={disabled}
                  hasError={hasError}
                  required={required}
                  size={size}
                >
                  <ComboboxPlaceholder
                    placeholder={placeholder}
                    floating={floating}
                    required={required}
                  >
                    <ComboboxValue />
                  </ComboboxPlaceholder>
                </ComboboxTrigger>
                <ComboboxContent className="rounded-lg">
                  <ComboboxList>
                    <ComboboxInput />
                    {emptyContent && <ComboboxEmpty>{emptyContent}</ComboboxEmpty>}
                    {options.map((option) => (
                      <ComboboxItem
                        disabled={option.disabled}
                        value={option.value || ''}
                        keywords={option.keywords}
                        label={option.label}
                        key={option.value}
                      >
                        {option.label}
                      </ComboboxItem>
                    ))}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </FormControl>
            {!hideErrorMessage && <FormMessage />}
          </FormItem>
        );
      }}
      control={formControl}
      name={name}
    />
  );
}
